import { useState } from 'react';
import type { ApiClient, ResearchResult } from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { sx } from './designStyles';

// Live web research, restored from production (widgets/live_research.dart): what is
// reported about a market, a facility or an operator, each finding with its sources, and
// one line on what it means. Perplexity finds; Claude reads. It runs only when asked, and
// says so when it could not run (no key, a timeout): nothing is invented.

const BTN = 'display:inline-flex;align-items:center;height:28px;padding:0 11px;border-radius:7px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;';

export function LiveResearch({ title, runLabel, load, testId }: {
  title: string;
  runLabel: string;
  load: (client: ApiClient, refresh: boolean) => Promise<ResearchResult>;
  testId?: string;
}) {
  const { client } = useSession();
  const [r, setR] = useState<ResearchResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const go = async (refresh: boolean) => {
    setBusy(true);
    setErr(null);
    try { setR(await load(client, refresh)); } catch (e) { setErr(e instanceof Error ? e.message : 'Research failed.'); } finally { setBusy(false); }
  };
  return (
    <div data-testid={testId} style={sx('display:flex;flex-direction:column;gap:6px;min-width:0;border-top:1px solid rgba(34,39,46,.12);padding-top:8px;')}>
      <div style={sx('display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;')}>
        <span style={sx('font-size:13px;font-weight:800;color:#22272E;')}>{title}</span>
        <span style={sx('font-size:14px;color:#54606C;')}>live web research · Perplexity + Claude</span>
        <span style={sx('flex:1;')} />
        <a href="#" aria-disabled={busy || undefined} onClick={(e) => { e.preventDefault(); if (!busy) void go(!!r); }} style={sx(BTN + 'background:#2F5C97;color:#fff;')}>
          {busy ? 'Researching…' : r ? 'Refresh' : runLabel}
        </a>
      </div>
      {err && <span role="alert" style={sx('font-size:14px;color:#B3261E;')}>{err}</span>}
      {r && (
        <>
          {r.verdict && <div style={sx('font-size:13px;font-weight:600;color:#22272E;line-height:1.45;')}>{r.verdict.line}</div>}
          {r.findings.map((f, i) => (
            <div key={i} style={sx('font-size:16px;color:#3C454F;line-height:1.45;')}>
              <span style={sx(f.material ? 'font-weight:700;color:#22272E;' : '')}>{f.text}</span>
              {(f.date || f.sourceDate) && <span style={sx('color:#54606C;')}>{' · ' + (f.date ?? f.sourceDate)}</span>}
              {f.sources.slice(0, 2).map((s, j) => (
                <a key={j} href={s} target="_blank" rel="noreferrer" style={sx('margin-left:6px;font-size:14px;color:#2F5C97;')}>{'[' + (j + 1) + ']'}</a>
              ))}
            </div>
          ))}
          {!r.findings.length && <span style={sx('font-size:14px;color:#54606C;')}>{r.note ?? 'Nothing reported in the window searched.'}</span>}
          <span style={sx('font-size:14px;color:#54606C;')}>
            {(r.live ? 'Researched ' : 'Not live: ') + new Date(r.asOf).toLocaleDateString() + (r.cached ? ' · from the day’s cache' : '') + (r.verdict?.provider ? ' · read by ' + r.verdict.provider : '') + (r.note && r.findings.length ? ' · ' + r.note : '')}
          </span>
        </>
      )}
    </div>
  );
}
